import { Injectable } from "@angular/core";
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import {
  catchError,
  from,
  Observable,
  switchMap,
  throwError,
  Subject,
  filter,
  take,
  finalize,
} from "rxjs";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { GlobalErrorHandler } from "@dmms/core/error-handling";
import { LocalizationService } from "@dmms/core/localization";
import { AuthProviderEnum } from "@models/core/auth";
import { AccessToken } from "@models/http/http-options";
import { AuthService } from "src/app/core";

@Injectable({
  providedIn: "root",
})
export class HttpInterceptorService implements HttpInterceptor {
  private isRefreshingToken = false;
  private refreshTokenSubject: Subject<string | null> = new Subject<
    string | null
  >();

  constructor(
    private readonly errorHandler: GlobalErrorHandler,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly localizationService: LocalizationService,
    private readonly translateService: TranslateService,
    private readonly authService: AuthService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(this.addHeaders(request)).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          return this.handleResponseError(error, request, next);
        }

        return throwError(() => error);
      }),
    );
  }

  addHeaders(req: HttpRequest<any>): HttpRequest<any> {
    const token = localStorage.getItem(AccessToken);
    const idp = localStorage.getItem("idp");
    const currentLang = this.localizationService.getCurrentLang();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let headers = req.headers;

    if (token) {
      headers = headers.set("Authorization", "Bearer " + token);
    }

    if (idp) {
      const providerHeader =
        idp === AuthProviderEnum.Okta
          ? AuthProviderEnum.Okta
          : AuthProviderEnum.AzureAD;
      headers = headers.set("X-Auth-Provider", providerHeader);
    }

    headers = headers.set("Lang", currentLang).set("Time-Zone", timeZone);

    return req.clone({ headers });
  }

  handleResponseError(
    error: any,
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!(error instanceof HttpErrorResponse)) return throwError(() => error);

    this.handleSpecificApiErrors(error);

    switch (error.status) {
      case 401:
        return this.handleUnauthorized(req, next);
      case 403:
        return this.handleForbidden(error);
      case 504:
        return this.handleGatewayTimeout();
      default:
        return throwError(() => error);
    }
  }

  handleUnauthorized(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const currentUrl = this.router.url;
    const isAuthPage = currentUrl.startsWith("/auth");

    if (isAuthPage) {
      return throwError(() => new Error("Unauthorized"));
    }

    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.refreshTokenSubject = new Subject<string | null>();

      return from(this.authService.getTokens()).pipe(
        switchMap((newToken) => {
          this.isRefreshingToken = false;
          if (!newToken) {
            this.refreshTokenSubject.next(null);
            this.refreshTokenSubject.complete();
            this.dialog.closeAll();
            this.router.navigate(["/auth"], {
              queryParams: { returnUrl: currentUrl },
            });
            return throwError(() => new Error("Unauthorized"));
          }
          this.refreshTokenSubject.next(newToken);
          this.refreshTokenSubject.complete();
          // Retry original request with the new token
          const retryRequest = request.clone({
            headers: request.headers.set("Authorization", "Bearer " + newToken),
          });
          return next.handle(retryRequest);
        }),
        catchError((err) => {
          this.isRefreshingToken = false;
          this.refreshTokenSubject.next(null);
          this.refreshTokenSubject.complete();
          this.dialog.closeAll();
          this.router.navigate(["/auth"], {
            queryParams: { returnUrl: currentUrl },
          });
          return throwError(() => err);
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        }),
      );
    } else {
      // Wait for the refresh to complete, then retry or fail
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== undefined),
        take(1),
        switchMap((token) => {
          if (!token) {
            return throwError(() => new Error("Unauthorized"));
          }
          const retryRequest = request.clone({
            headers: request.headers.set("Authorization", "Bearer " + token),
          });
          return next.handle(retryRequest);
        }),
      );
    }
  }

  handleSpecificApiErrors(error: HttpErrorResponse) {
    const errorUrl = error.url ?? "";
    const shouldHandleError =
      error.status !== 0 &&
      !errorUrl.includes("api/events/raw") &&
      !errorUrl.includes("api/v1/administration/user/current-user") &&
      error.status !== 401;

    if (shouldHandleError) {
      this.errorHandler.handleError(error);
    }
  }

  handleForbidden(error: any) {
    const hasAbsentPermissions = error?.error?.errors?.some(
      (err: any) => err?.tags?.AbsentPermissions,
    );

    if (!hasAbsentPermissions && error?.error?.errors?.length) {
      return throwError(() => error);
    }

    const allPermissions = error?.error?.errors
      ?.flatMap((error) => error?.tags?.AbsentPermissions?.split(",") ?? [])
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    const uniquePermissions = Array.from(new Set(allPermissions)).join(", ");

    let text = this.translateService.instant("no.rights");

    if (uniquePermissions?.length) {
      text +=
        this.translateService.instant("required.rights") +
        " " +
        uniquePermissions;
    }

    return throwError(() => new Error(text));
  }

  handleGatewayTimeout() {
    return throwError(
      () =>
        new Error(
          "Gateway Timeout: Во время выполнения запроса произошла непредвиденная ошибка",
        ),
    );
  }
}
